/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { type MouseEvent, useEffect, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import IconButton from '@atlaskit/button/icon/button';
import LinkButton from '@atlaskit/button/link';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import VideoIcon from '@atlaskit/icon/core/video';
import Image from '@atlaskit/image';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { token } from '@atlaskit/tokens';
import { jiraRegisteredContent } from './content';

import { type TemplateXflowReminderState } from '@post-office/growth-common/confluence-experiments-context';

import aiNotesIcon from './assets/ai-notes.svg';

type RecommendedApp = 'confluence' | 'jira' | 'loom';
type ActionTier = 'high' | 'medium' | 'low';
type SurfaceProduct = 'jira' | 'confluence';

const styles = cssMap({
	root: {
		width: '100%',
		backgroundColor: token('color.background.accent.blue.subtlest'),
		borderRadius: token('radius.large'),
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		// Horizontal margins were causing the banner to exceed the side-nav width
		// (width: 100% + horizontal margins overflows the container). Keep only a
		// minimal vertical gap from the surrounding nav items.
		marginInline: 0,
		marginBlock: token('space.050'),
	},
	layout: {
		display: 'flex',
		alignItems: 'flex-start',
		gap: token('space.100'),
	},
	logoWrapper: {
		flexShrink: 0,
	},
	content: {
		flexGrow: 1,
		minWidth: 0,
	},
	dismiss: {
		flexShrink: 0,
	},
	action: {
		alignItems: 'center',
		backgroundColor: 'transparent',
		color: token('color.link'),
		columnGap: token('space.075'),
		display: 'inline-flex',
		fontWeight: token('font.weight.medium'),
		paddingTop: 0,
		paddingRight: 0,
		paddingBottom: 0,
		paddingLeft: 0,
		textDecoration: 'none',
		'&:visited': {
			color: token('color.link'),
		},
		'&:hover': {
			color: token('color.link'),
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
		'&:focus': {
			color: token('color.link'),
		},
	},
});

const subheadingByApp: Record<
	RecommendedApp,
	(typeof jiraRegisteredContent.personalized)[keyof typeof jiraRegisteredContent.personalized]
> = {
	confluence: jiraRegisteredContent.personalized.subheadingConfluence,
	jira: jiraRegisteredContent.personalized.subheadingJira,
	loom: jiraRegisteredContent.personalized.subheadingLoom,
};

const destinationTypeByApp: Record<RecommendedApp, string> = {
	confluence: 'create_with_rovo',
	jira: 'jira_onboarding',
	loom: 'loom_meetings',
};

export type PersonalizedBannerProps = {
	heading?: string;
	href: string;
	locale?: string;
	recommendedApp: RecommendedApp;
	messageInstanceId?: string;
	onClose?: () => void;
	onCtaClick?: (event: MouseEvent<HTMLElement>) => void;
	ctaLabel?: string;
	secondaryActionLabel?: string;
	onSecondaryAction?: () => void;
	experimentKey?: string;
	actionTier?: ActionTier;
	targetTenantId?: string;
	collectionEntitlementId?: string;
	surfaceProduct?: SurfaceProduct;
	blueprintModuleCompleteKey?: string;
	messageTemplateId?: string;
	placementId?: string;
	promptSurface?: string;
	reminderState?: TemplateXflowReminderState;
};

export const PersonalizedBanner = ({
	heading,
	href,
	locale,
	recommendedApp,
	messageInstanceId,
	onClose,
	onCtaClick,
	ctaLabel,
	secondaryActionLabel,
	onSecondaryAction,
	experimentKey,
	actionTier,
	targetTenantId,
	collectionEntitlementId,
	surfaceProduct,
	blueprintModuleCompleteKey,
	messageTemplateId,
	placementId,
	promptSurface,
	reminderState,
}: PersonalizedBannerProps): JSX.Element => {
	const { formatMessage } = useIntl();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const analyticsAttributes = useMemo(
		() => ({
			recommendedApp,
			messageInstanceId,
			locale,
			experimentKey,
			actionTier,
			targetTenantId,
			collectionEntitlementId,
			surfaceProduct,
			destinationType: destinationTypeByApp[recommendedApp],
			arm: 'personalized' as const,
			blueprintModuleCompleteKey,
			messageTemplateId,
			placementId,
			promptSurface,
			reminderState,
		}),
		[
			recommendedApp,
			messageInstanceId,
			locale,
			experimentKey,
			actionTier,
			targetTenantId,
			collectionEntitlementId,
			surfaceProduct,
			blueprintModuleCompleteKey,
			messageTemplateId,
			placementId,
			promptSurface,
			reminderState,
		],
	);

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'twcPersonalizedBanner',
			attributes: analyticsAttributes,
		}).fire('growth');
	}, [analyticsAttributes, createAnalyticsEvent]);

	const handleClose = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcPersonalizedBannerClose',
			attributes: analyticsAttributes,
		}).fire('growth');
		onClose?.();
	};

	const handleCtaClick = (clickEvent: MouseEvent<HTMLElement>) => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcPersonalizedBannerCta',
			attributes: analyticsAttributes,
		}).fire('growth');
		onCtaClick?.(clickEvent);
	};

	const handleSecondaryAction = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcPersonalizedBannerSecondaryAction',
			attributes: analyticsAttributes,
		}).fire('growth');
		onSecondaryAction?.();
	};

	return (
		<Box xcss={styles.root} testId="twc-personalized-banner">
			<Box xcss={styles.layout} testId="twc-personalized-banner-layout">
				<Box xcss={styles.logoWrapper} testId="twc-personalized-banner-logo">
					<Image
						src={aiNotesIcon}
						srcDark={aiNotesIcon}
						alt=""
						testId="twc-personalized-banner-ai-notes-icon"
						width={40}
						height={40}
					/>
				</Box>
				<Stack xcss={styles.content} space="space.100" testId="twc-personalized-banner-content">
					<Stack space="space.100">
						<Heading as="h3" size="xsmall" testId="twc-personalized-banner-heading">
							{heading ?? formatMessage(jiraRegisteredContent.personalized.heading)}
						</Heading>
						<Text
							as="p"
							size="medium"
							color="color.text.subtle"
							maxLines={2}
							testId="twc-personalized-banner-subheading"
						>
							{formatMessage(subheadingByApp[recommendedApp])}
						</Text>
					</Stack>
					{secondaryActionLabel ? (
						<Inline alignBlock="center" space="space.100" shouldWrap>
							<Anchor
								href={href}
								onClick={handleCtaClick}
								target="_blank"
								testId="twc-personalized-banner-cta"
								xcss={styles.action}
							>
								<VideoIcon label="" />
								{ctaLabel ?? formatMessage(jiraRegisteredContent.personalized.ctaButton)}
							</Anchor>
							<Inline alignBlock="center" space="space.100">
								<Text aria-hidden={true}>·</Text>
								<Pressable
									onClick={handleSecondaryAction}
									testId="twc-personalized-banner-secondary-action"
									xcss={styles.action}
								>
									{secondaryActionLabel}
								</Pressable>
							</Inline>
						</Inline>
					) : (
						<LinkButton
							shouldFitContainer
							href={href}
							testId="twc-personalized-banner-cta"
							onClick={handleCtaClick}
							target="_blank"
						>
							{ctaLabel ?? formatMessage(jiraRegisteredContent.personalized.ctaButton)}
						</LinkButton>
					)}
				</Stack>
				<Box xcss={styles.dismiss} testId="twc-personalized-banner-dismiss">
					<IconButton
						icon={(iconProps) => <CrossIcon {...iconProps} size="medium" />}
						onClick={handleClose}
						label={formatMessage(jiraRegisteredContent.personalized.closeLabel)}
						testId="twc-personalized-banner-close"
						appearance="subtle"
					/>
				</Box>
			</Box>
		</Box>
	);
};
